import React, { useState, useEffect, useRef } from 'react'
import { useSearchParams } from 'react-router-dom'
import { loadStripe } from '@stripe/stripe-js'
import { Elements, PaymentElement, useStripe, useElements } from '@stripe/react-stripe-js'
import { useAuth } from '../../../context/AuthProvider'
import { api } from '../../../lib/api'
import { Button } from '../../../components/ui/button'
import FundUseDisclosure from '../../../components/giving/FundUseDisclosure'
import FeeDisclosure from '../../../components/giving/FeeDisclosure'
import { pickInitialRoundupParishId } from '../../../lib/roundupParish'
import {
  Loader2,
  Building2,
  CreditCard,
  RefreshCw,
  Unlink,
  PlusCircle,
  ArrowUpCircle,
  TrendingUp,
  DollarSign,
  AlertCircle,
  CheckCircle,
  XCircle,
  Church,
  Settings,
  Wallet,
  Zap,
  ChevronDown,
  ChevronUp,
} from 'lucide-react'

const STRIPE_PUBLISHABLE_KEY = process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY || ''
const stripePromise = STRIPE_PUBLISHABLE_KEY ? loadStripe(STRIPE_PUBLISHABLE_KEY) : null
const VISIBLE_TX_COUNT = 3

const STRIPE_APPEARANCE = {
  theme: 'stripe',
  variables: {
    colorPrimary: '#D4AF37',
    colorBackground: '#ffffff',
    colorText: '#1A1A1A',
    fontFamily: 'Inter, system-ui, sans-serif',
  },
}

// ─── Financial Connections Link Button ───────────────────────────────────────
function FinancialConnectionsButton({ onSuccess, onExit, label = 'Link a Bank', className }) {
  const [loading, setLoading] = useState(false)

  const handleClick = async () => {
    try {
      setLoading(true)
      const stripe = await stripePromise
      if (!stripe) throw new Error('Stripe failed to load')
      const { clientSecret, sessionId } = await api.createFinancialConnectionsSession()
      const result = await stripe.collectFinancialConnectionsAccounts({ clientSecret })
      if (result.error) {
        if (onExit) onExit(result.error)
        return
      }
      const accounts = result.financialConnectionsSession?.accounts || []
      if (accounts.length === 0) {
        if (onExit) onExit()
        return
      }
      await onSuccess(sessionId)
    } catch (err) {
      console.error('Financial Connections link failed:', err)
      if (onExit) onExit(err)
    } finally {
      setLoading(false)
    }
  }

  return (
    <Button
      onClick={handleClick}
      disabled={loading}
      className={className || 'bg-gold hover:bg-gold text-black font-semibold'}
    >
      {loading ? (
        <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Connecting...</>
      ) : (
        <><PlusCircle className="w-4 h-4 mr-2" />{label}</>
      )}
    </Button>
  )
}

// ─── Stripe Payment Method Form ──────────────────────────────────────────────
function PaymentMethodForm({ onSuccess, onCancel }) {
  const stripe = useStripe()
  const elements = useElements()
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState(null)

  const handleSubmit = async (e) => {
    e.preventDefault()
    if (!stripe || !elements) return
    try {
      setSubmitting(true)
      setError(null)
      const { error: submitError } = await elements.submit()
      if (submitError) { setError(submitError.message); return }
      const { setupIntent, error: confirmError } = await stripe.confirmSetup({
        elements,
        confirmParams: { return_url: window.location.href },
        redirect: 'if_required',
      })
      if (confirmError) { setError(confirmError.message); return }
      if (setupIntent?.id) {
        await api.saveRoundupPaymentMethod(setupIntent.id)
        onSuccess()
      }
    } catch (err) {
      setError(err.message || 'Failed to save payment method')
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <PaymentElement />
      {error && (
        <p className="text-red-600 text-sm flex items-center gap-1">
          <AlertCircle className="w-4 h-4" /> {error}
        </p>
      )}
      <div className="flex gap-3">
        <Button type="submit" disabled={!stripe || submitting} className="bg-gold hover:bg-gold text-black font-semibold">
          {submitting ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Saving...</> : 'Save Card'}
        </Button>
        <Button type="button" variant="ghost" onClick={onCancel} className="text-charcoal-muted">Cancel</Button>
      </div>
    </form>
  )
}

// ─── Parish Selector ─────────────────────────────────────────────────────────
function ParishSelector({ parishes, selectedId, onSelect }) {
  if (parishes.length === 0) {
    return (
      <p className="text-charcoal-faint text-sm">
        No parishes found. Join a parish first from the Parishes page.
      </p>
    )
  }
  const selected = parishes.find((p) => p.id === selectedId)
  return (
    <div className="flex flex-col gap-1.5">
      {parishes.map((p) => (
        <button
          key={p.id}
          onClick={() => p.stripeReady && onSelect(p.id)}
          disabled={!p.stripeReady}
          className={`flex items-center gap-3 w-full rounded px-3 py-2.5 text-left transition-all ${
            !p.stripeReady
              ? 'bg-surface-linen border border-surface-border cursor-not-allowed opacity-50'
              : selectedId === p.id
                ? 'bg-gold/10 border border-gold/40'
                : 'bg-white border border-surface-border hover:border-gold/30 hover:bg-surface-linen'
          }`}
        >
          <div className={`w-7 h-7 rounded-full flex items-center justify-center shrink-0 ${selectedId === p.id ? 'bg-gold/20' : 'bg-charcoal/5'}`}>
            <Church className={`w-3.5 h-3.5 ${selectedId === p.id ? 'text-gold' : 'text-charcoal-faint'}`} />
          </div>
          <div className="flex flex-col min-w-0 flex-1">
            <span className={`text-sm font-medium ${selectedId === p.id ? 'text-charcoal' : 'text-charcoal-muted'}`}>
              {p.name}
            </span>
            {!p.stripeReady && (
              <span className="text-[11px] text-red-600/70 flex items-center gap-1 mt-0.5">
                <AlertCircle className="w-3 h-3 shrink-0" />{p.givingUnavailable ? "Isn't accepting donations right now" : 'Payment setup incomplete'}
              </span>
            )}
          </div>
          {p.stripeReady && selectedId === p.id && (
            <CheckCircle className="w-4 h-4 text-gold shrink-0" />
          )}
        </button>
      ))}
      {selected && selected.stripeReady && (
        <>
          <FundUseDisclosure parishName={selected.name} fundUse={selected.fundUse} />
          <FeeDisclosure
            percent={selected.giving?.fee?.roundupPercent}
            fixedCents={selected.giving?.fee?.fixedCents}
            parishName={selected.name}
          />
        </>
      )}
    </div>
  )
}

// ─── Bank List ────────────────────────────────────────────────────────────────
function BankList({ banks, unlinking, onUnlink, compact = false }) {
  if (banks.length === 0) return null
  return (
    <div className="space-y-2">
      {banks.map((bank) => (
        <div
          key={bank.id}
          className="flex items-center justify-between bg-surface-linen border border-surface-border rounded p-3"
        >
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 rounded-full bg-gold/10 flex items-center justify-center shrink-0">
              <Building2 className="w-4 h-4 text-gold" />
            </div>
            <div>
              <p className="text-charcoal text-sm font-medium">{bank.institutionName || 'Unknown Bank'}</p>
              <p className="text-charcoal-faint text-xs">
                {bank.accounts.map((a) => `${a.name || a.subtype || 'Account'}${a.mask ? ` ••${a.mask}` : ''}`).join(', ')}
              </p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            {bank.isActive
              ? <span className="text-xs text-emerald-400 flex items-center gap-1"><CheckCircle className="w-3.5 h-3.5" />Active</span>
              : <span className="text-xs text-red-600 flex items-center gap-1"><XCircle className="w-3.5 h-3.5" />Inactive</span>
            }
            {onUnlink && (
              <Button
                variant="ghost"
                size="sm"
                onClick={() => onUnlink(bank.id, bank.institutionName)}
                disabled={unlinking === bank.id}
                className="text-red-600/70 hover:text-red-600 hover:bg-red-50"
              >
                {unlinking === bank.id ? <Loader2 className="w-4 h-4 animate-spin" /> : <Unlink className="w-4 h-4" />}
              </Button>
            )}
          </div>
        </div>
      ))}
    </div>
  )
}

// ─── Main Component ───────────────────────────────────────────────────────────
export default function DonorRoundups() {
  const { user } = useAuth()
  const [searchParams] = useSearchParams()
  const requestedParishId = searchParams.get('parishId')
  const initialParishPicked = useRef(false)

  // Data state
  const [banks, setBanks] = useState([])
  const [transactions, setTransactions] = useState([])
  const [totalCount, setTotalCount] = useState(0)
  const [totalRoundupAmount, setTotalRoundupAmount] = useState(0)
  const [undonatedRoundupAmount, setUndonatedRoundupAmount] = useState(0)
  const [undonatedCount, setUndonatedCount] = useState(0)
  const [allTimeRoundupTotal, setAllTimeRoundupTotal] = useState(0)
  const [allTimeRoundupCount, setAllTimeRoundupCount] = useState(0)
  const [roundupSettings, setRoundupSettings] = useState(null)
  const [userParishes, setUserParishes] = useState([])

  // Loading state
  const [loadingBanks, setLoadingBanks] = useState(true)
  const [loadingTransactions, setLoadingTransactions] = useState(true)
  const [loadingSettings, setLoadingSettings] = useState(true)
  const [loadingParishes, setLoadingParishes] = useState(true)
  const [loadingRoundupTotal, setLoadingRoundupTotal] = useState(true)

  // Action state
  const [syncing, setSyncing] = useState(false)
  const [unlinking, setUnlinking] = useState(null)
  const [exchanging, setExchanging] = useState(false)
  const [savingSettings, setSavingSettings] = useState(false)
  const [loadingSetupIntent, setLoadingSetupIntent] = useState(false)

  // Form state
  const [selectedParishId, setSelectedParishId] = useState('')
  const [boostFactor, setBoostFactor] = useState(1)
  const [showPaymentForm, setShowPaymentForm] = useState(false)
  const [setupIntentSecret, setSetupIntentSecret] = useState(null)

  // UI state
  const [wizardStep, setWizardStep] = useState(0) // 0 = not started, 1 = payment+boost, 2 = banks
  const [showEditSettings, setShowEditSettings] = useState(false)
  const [showAllTransactions, setShowAllTransactions] = useState(false)
  const [error, setError] = useState(null)
  const [successMsg, setSuccessMsg] = useState(null)

  useEffect(() => {
    fetchBanks()
    fetchTransactions()
    fetchRoundupSettings()
    fetchUserParishes()
    fetchAllTimeRoundupTotal()
  }, [])

  useEffect(() => {
    if (successMsg) {
      const t = setTimeout(() => setSuccessMsg(null), 5000)
      return () => clearTimeout(t)
    }
  }, [successMsg])

  // Once settings and parishes have both loaded, choose the parish a single
  // time: ?parishId= (app handoff) > saved parish > first Stripe-ready parish.
  useEffect(() => {
    if (loadingSettings || loadingParishes || initialParishPicked.current) return
    initialParishPicked.current = true
    const savedId = roundupSettings?.roundupParishId
    const picked = pickInitialRoundupParishId({ requestedId: requestedParishId, savedId, parishes: userParishes })
    if (picked) setSelectedParishId(picked)
    if (roundupSettings?.roundupsEnabled && picked && picked !== savedId) setShowEditSettings(true)
  }, [loadingSettings, loadingParishes, roundupSettings, userParishes, requestedParishId])

  // ─── Fetchers ─────────────────────────────────────────────────────────────

  const fetchBanks = async () => {
    try {
      setLoadingBanks(true)
      const data = await api.getLinkedBanks()
      setBanks(data)
    } catch (err) {
      console.error('Failed to fetch banks:', err)
    } finally {
      setLoadingBanks(false)
    }
  }

  const fetchTransactions = async () => {
    try {
      setLoadingTransactions(true)
      const data = await api.getBankTransactions()
      setTransactions(data.transactions || [])
      setTotalCount(data.totalCount || 0)
      setTotalRoundupAmount(data.totalRoundupAmount || 0)
      setUndonatedRoundupAmount(data.undonatedRoundupAmount || 0)
      setUndonatedCount(data.undonatedCount || 0)
    } catch (err) {
      console.error('Failed to fetch transactions:', err)
    } finally {
      setLoadingTransactions(false)
    }
  }

  const fetchRoundupSettings = async () => {
    try {
      setLoadingSettings(true)
      const data = await api.getRoundupSettings()
      setRoundupSettings(data)
      if (data.roundupBoostFactor) setBoostFactor(data.roundupBoostFactor)
    } catch (err) {
      console.error('Failed to fetch roundup settings:', err)
    } finally {
      setLoadingSettings(false)
    }
  }

  const fetchUserParishes = async () => {
    try {
      setLoadingParishes(true)
      const userData = await api.getUser()
      const parishes = userData.parishes || []
      const loadDetail = async (parishId) => {
        try {
          const full = await api.getParish(parishId)
          const givingUnavailable = full.giving?.mode === 'unavailable'
          let stripeReady = false
          if (!givingUnavailable) {
            try {
              const result = await api.stripeIsReady(full.id)
              stripeReady = result.isReady
            } catch {}
          }
          return { id: full.id, name: full.name, stripeReady, givingUnavailable, fundUse: full.giving?.fundUse, giving: full.giving }
        } catch {
          return { id: parishId, name: 'Parish', stripeReady: false, failed: true }
        }
      }
      const details = await Promise.all(parishes.map((p) => loadDetail(p.parishId)))
      if (requestedParishId && !details.some((d) => d.id === requestedParishId)) {
        const requested = await loadDetail(requestedParishId)
        // Only offer it if the lookup actually succeeded.
        if (!requested.failed) details.push(requested)
      }
      setUserParishes(details)
    } catch (err) {
      console.error('Failed to fetch parishes:', err)
    } finally {
      setLoadingParishes(false)
    }
  }

  const fetchAllTimeRoundupTotal = async () => {
    try {
      setLoadingRoundupTotal(true)
      if (!user?.id) return
      const data = await api.getUserDonations(user.id, { limit: 1, donationType: 'roundup' })
      setAllTimeRoundupTotal(data.meta?.totalAmountDollars || 0)
      setAllTimeRoundupCount(data.meta?.totalItems || 0)
    } catch (err) {
      console.error('Failed to fetch roundup total:', err)
    } finally {
      setLoadingRoundupTotal(false)
    }
  }

  // ─── Handlers ─────────────────────────────────────────────────────────────

  const handleLinkSuccess = async (sessionId) => {
    try {
      setExchanging(true)
      setError(null)
      await api.linkFinancialConnectionsAccounts(sessionId)
      setSuccessMsg('Bank linked successfully!')
      await Promise.all([fetchBanks(), fetchTransactions()])
    } catch (err) {
      setError(err.message || 'Failed to link bank account')
    } finally {
      setExchanging(false)
    }
  }

  const handleSync = async () => {
    try {
      setSyncing(true)
      setError(null)
      await api.syncBankTransactions()
      setSuccessMsg('Transactions synced!')
      await fetchTransactions()
    } catch (err) {
      setError(err.message || 'Failed to sync transactions')
    } finally {
      setSyncing(false)
    }
  }

  const handleUnlink = async (itemId, institutionName) => {
    if (!window.confirm(`Unlink ${institutionName || 'this bank'}? This will remove all associated transaction data.`)) return
    try {
      setUnlinking(itemId)
      setError(null)
      await api.unlinkBank(itemId)
      setSuccessMsg(`${institutionName || 'Bank'} unlinked successfully`)
      await Promise.all([fetchBanks(), fetchTransactions()])
    } catch (err) {
      setError(err.message || 'Failed to unlink bank')
    } finally {
      setUnlinking(null)
    }
  }

  const handleSaveSettings = async (enabled) => {
    try {
      setSavingSettings(true)
      setError(null)
      const data = await api.updateRoundupSettings({
        roundupsEnabled: enabled,
        roundupParishId: selectedParishId || undefined,
        roundupBoostFactor: boostFactor,
      })
      setRoundupSettings((prev) => ({ ...prev, ...data }))
      setSuccessMsg(enabled ? 'Roundups enabled!' : 'Roundups disabled')
    } catch (err) {
      setError(err.message || 'Failed to update roundup settings')
    } finally {
      setSavingSettings(false)
    }
  }

  const handleSetupPaymentMethod = async () => {
    try {
      setLoadingSetupIntent(true)
      setError(null)
      const data = await api.createRoundupSetupIntent()
      setSetupIntentSecret(data.clientSecret)
      setShowPaymentForm(true)
    } catch (err) {
      setError(err.message || 'Failed to start payment method setup')
    } finally {
      setLoadingSetupIntent(false)
    }
  }

  const handlePaymentMethodSaved = async () => {
    setShowPaymentForm(false)
    setSetupIntentSecret(null)
    setSuccessMsg('Payment method saved!')
    await fetchRoundupSettings()
  }

  const handleWizardStep1Next = async () => {
    try {
      setSavingSettings(true)
      await api.updateRoundupSettings({
        roundupsEnabled: false,
        roundupParishId: selectedParishId || undefined,
        roundupBoostFactor: boostFactor,
      })
    } catch {
      // Non-blocking — proceed to step 2
    } finally {
      setSavingSettings(false)
    }
    setWizardStep(2)
  }

  const handleWizardFinish = async () => {
    await handleSaveSettings(true)
    setWizardStep(0)
  }

  // ─── Computed values ──────────────────────────────────────────────────────

  const formatCurrency = (amount) =>
    new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(amount)

  const formatDate = (dateStr) =>
    new Date(dateStr + 'T00:00:00').toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' })

  const effectiveBoost = roundupSettings?.roundupBoostFactor || boostFactor || 1
  const boostedUndonatedAmount = Math.round(undonatedRoundupAmount * effectiveBoost * 100) / 100

  const hasUpcomingDonation =
    roundupSettings?.roundupsEnabled &&
    roundupSettings?.roundupParishId &&
    roundupSettings?.hasPaymentMethod

  const nextRoundupSunday = (() => {
    const now = new Date()
    const dow = now.getUTCDay()
    const days = (7 - dow) % 7
    const d = new Date(Date.UTC(
      now.getUTCFullYear(),
      now.getUTCMonth(),
      now.getUTCDate() + days,
      9, 0, 0, 0,
    ))
    return d.getTime() <= now.getTime() ? new Date(d.getTime() + 7 * 24 * 3600 * 1000) : d
  })()

  const nextRoundupWindow = (() => {
    // The Sun–Sat window the next run will process. Must match the backend
    // (FinancialConnectionsService.nextRoundupWindow / RoundupWindowService):
    // 14 days before the next Sunday run, NOT 21 — otherwise the displayed
    // window label drifts a week behind the transactions actually returned.
    const run = nextRoundupSunday
    const startMs = Date.UTC(run.getUTCFullYear(), run.getUTCMonth(), run.getUTCDate(), 0, 0, 0, 0) - 14 * 24 * 3600 * 1000
    const endMs = startMs + 6 * 24 * 3600 * 1000
    return { start: new Date(startMs), end: new Date(endMs) }
  })()

  const formatWindowRange = (start, end) => {
    const opts = { month: 'short', day: 'numeric', timeZone: 'UTC' }
    return `${start.toLocaleDateString(undefined, opts)} – ${end.toLocaleDateString(undefined, opts)}`
  }

  const isConfigured = !loadingSettings && !!(roundupSettings?.hasPaymentMethod || roundupSettings?.roundupsEnabled)

  const filteredTxns = transactions.filter((txn) => txn.amount > 0)
  const visibleTxns = showAllTransactions ? filteredTxns : filteredTxns.slice(0, VISIBLE_TX_COUNT)

  const selectedParishSelectable = userParishes.some(
    (p) => p.id === selectedParishId && p.stripeReady && !p.givingUnavailable,
  )

  // ─── Loading splash ───────────────────────────────────────────────────────

  if (loadingSettings || loadingParishes) {
    return (
      <div className="flex items-center justify-center min-h-64">
        <Loader2 className="w-8 h-8 text-gold animate-spin" />
      </div>
    )
  }

  // ─── Inline status messages ───────────────────────────────────────────────

  const statusMessages = (
    <>
      {error && (
        <div className="bg-red-50 border border-red-200 rounded p-4 flex items-start gap-3">
          <AlertCircle className="w-5 h-5 text-red-600 mt-0.5 shrink-0" />
          <div>
            <p className="text-red-600 text-sm">{error}</p>
            <button onClick={() => setError(null)} className="text-red-600/70 text-xs mt-1 hover:text-red-600">
              Dismiss
            </button>
          </div>
        </div>
      )}
      {successMsg && (
        <div className="bg-emerald-500/10 border border-emerald-500/30 rounded p-4 flex items-center gap-3">
          <CheckCircle className="w-5 h-5 text-emerald-400 shrink-0" />
          <p className="text-emerald-700 text-sm">{successMsg}</p>
        </div>
      )}
      {exchanging && (
        <div className="bg-gold/10 border border-gold/30 rounded p-4 flex items-center gap-3">
          <Loader2 className="w-5 h-5 text-gold animate-spin shrink-0" />
          <p className="text-charcoal text-sm">Linking your bank account and syncing transactions...</p>
        </div>
      )}
    </>
  )

  // ─── WIZARD ───────────────────────────────────────────────────────────────

  if (wizardStep > 0) {
    return (
      <div className="max-w-2xl mx-auto space-y-6">
        <h1 className="text-3xl font-bold font-display text-charcoal">Set Up Round-Ups</h1>

        {statusMessages}

        {/* Step Indicator */}
        <div className="flex items-center gap-3">
          <div className={`flex items-center justify-center w-8 h-8 rounded-full text-sm font-bold transition-colors ${wizardStep >= 1 ? 'bg-gold text-black' : 'bg-surface-border text-charcoal-faint'}`}>
            1
          </div>
          <div className={`flex-1 h-0.5 transition-colors ${wizardStep >= 2 ? 'bg-gold' : 'bg-surface-border'}`} />
          <div className={`flex items-center justify-center w-8 h-8 rounded-full text-sm font-bold transition-colors ${wizardStep >= 2 ? 'bg-gold text-black' : 'bg-surface-border text-charcoal-faint'}`}>
            2
          </div>
        </div>

        {/* ── Step 1: Payment + Boost ──────────────────────────────────── */}
        {wizardStep === 1 && (
          <div className="space-y-5">
            <div>
              <h2 className="text-xl font-semibold text-charcoal mb-1">Payment & Preferences</h2>
              <p className="text-charcoal-muted text-sm">Select your parish, add a payment card, and set your boost multiplier.</p>
            </div>

            {/* Parish */}
            <div className="bg-white border border-surface-border rounded p-5">
              <div className="flex items-center gap-2 mb-3">
                <Church className="w-4 h-4 text-gold" />
                <h3 className="font-semibold text-charcoal text-sm">Donate roundups to</h3>
              </div>
              <ParishSelector
                parishes={userParishes}
                selectedId={selectedParishId}
                onSelect={setSelectedParishId}
              />
            </div>

            {/* Boost Factor */}
            <div className="bg-white border border-surface-border rounded p-5">
              <div className="flex items-center gap-2 mb-3">
                <Zap className="w-4 h-4 text-gold" />
                <h3 className="font-semibold text-charcoal text-sm">Roundup Boost</h3>
              </div>
              <div className="flex items-center gap-2 flex-wrap">
                {[1, 2, 3, 5, 10].map((factor) => (
                  <button
                    key={factor}
                    onClick={() => setBoostFactor(factor)}
                    className={`px-4 py-2 rounded text-sm font-semibold transition-all ${
                      boostFactor === factor
                        ? 'bg-gold text-black shadow-gold'
                        : 'bg-surface-linen text-charcoal-muted hover:bg-surface-border hover:text-charcoal border border-surface-border'
                    }`}
                  >
                    {factor === 1 ? '1× Standard' : `${factor}×`}
                  </button>
                ))}
              </div>
              {boostFactor > 1 && (
                <p className="text-gold text-xs mt-2 flex items-center gap-1">
                  <Zap className="w-3 h-3" />
                  Your roundups will be multiplied {boostFactor}×
                </p>
              )}
            </div>

            {/* Payment Method */}
            <div className="bg-white border border-surface-border rounded p-5">
              <div className="flex items-center gap-2 mb-3">
                <CreditCard className="w-4 h-4 text-gold" />
                <h3 className="font-semibold text-charcoal text-sm">Payment Method</h3>
              </div>
              {roundupSettings?.hasPaymentMethod && !showPaymentForm ? (
                <div className="flex items-center gap-3">
                  <span className="text-emerald-400 text-sm flex items-center gap-1.5">
                    <CheckCircle className="w-4 h-4" /> Card saved
                  </span>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={handleSetupPaymentMethod}
                    disabled={loadingSetupIntent}
                    className="text-charcoal-muted hover:text-gold text-xs"
                  >
                    {loadingSetupIntent && <Loader2 className="w-3 h-3 animate-spin mr-1" />}
                    Change card
                  </Button>
                </div>
              ) : showPaymentForm && setupIntentSecret && stripePromise ? (
                <Elements stripe={stripePromise} options={{ clientSecret: setupIntentSecret, appearance: STRIPE_APPEARANCE }}>
                  <PaymentMethodForm
                    onSuccess={handlePaymentMethodSaved}
                    onCancel={() => { setShowPaymentForm(false); setSetupIntentSecret(null) }}
                  />
                </Elements>
              ) : (
                <Button
                  onClick={handleSetupPaymentMethod}
                  disabled={loadingSetupIntent}
                  className="bg-gold hover:bg-gold text-black font-semibold"
                >
                  {loadingSetupIntent
                    ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Loading...</>
                    : <><CreditCard className="w-4 h-4 mr-2" />Add a card</>
                  }
                </Button>
              )}
            </div>

            <div className="flex items-center justify-between pt-2">
              <Button variant="ghost" onClick={() => setWizardStep(0)} className="text-charcoal-muted">
                ← Back
              </Button>
              <Button
                onClick={handleWizardStep1Next}
                disabled={!selectedParishSelectable || !roundupSettings?.hasPaymentMethod || savingSettings}
                className="bg-gold hover:bg-gold text-black font-semibold px-6"
              >
                {savingSettings && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                Next: Link Banks →
              </Button>
            </div>
          </div>
        )}

        {/* ── Step 2: Banks ─────────────────────────────────────────────── */}
        {wizardStep === 2 && (
          <div className="space-y-5">
            <div>
              <h2 className="text-xl font-semibold text-charcoal mb-1">Link Your Banks</h2>
              <p className="text-charcoal-muted text-sm">
                Connect bank accounts so we can track your transactions and calculate round-ups automatically.
              </p>
            </div>

            <div className="bg-white border border-surface-border rounded p-5">
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-2">
                  <Building2 className="w-4 h-4 text-gold" />
                  <h3 className="font-semibold text-charcoal text-sm">Linked Banks</h3>
                </div>
                <FinancialConnectionsButton onSuccess={handleLinkSuccess} />
              </div>

              {loadingBanks ? (
                <div className="flex items-center justify-center py-6">
                  <Loader2 className="w-5 h-5 text-gold animate-spin" />
                </div>
              ) : banks.length === 0 ? (
                <div className="text-center py-8">
                  <Building2 className="w-12 h-12 text-charcoal-faint mx-auto mb-3" />
                  <p className="text-charcoal-subtle text-sm">No banks linked yet.</p>
                  <p className="text-charcoal-faint text-xs mt-1">
                    Link at least one bank to track round-ups automatically.
                  </p>
                </div>
              ) : (
                <BankList banks={banks} unlinking={null} onUnlink={null} />
              )}
            </div>

            <div className="flex items-center justify-between pt-2">
              <Button variant="ghost" onClick={() => setWizardStep(1)} className="text-charcoal-muted">
                ← Back
              </Button>
              <Button
                onClick={handleWizardFinish}
                disabled={savingSettings}
                className="bg-gold hover:bg-gold text-black font-semibold px-6"
              >
                {savingSettings
                  ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Saving...</>
                  : <><CheckCircle className="w-4 h-4 mr-2" />Finish Setup</>
                }
              </Button>
            </div>
          </div>
        )}
      </div>
    )
  }

  // ─── UNCONFIGURED ─────────────────────────────────────────────────────────

  if (!isConfigured) {
    return (
      <div className="space-y-4">
        <h1 className="text-3xl font-bold font-display text-charcoal">Round-Ups</h1>
        {statusMessages}
        <div className="flex flex-col items-center justify-center min-h-[60vh] text-center">
          <div className="w-20 h-20 rounded-full bg-gold/10 flex items-center justify-center mb-6">
            <Zap className="w-10 h-10 text-gold" />
          </div>
          <h2 className="text-2xl font-bold font-display text-charcoal mb-3">Round-Up Donations</h2>
          <p className="text-charcoal-muted max-w-sm mb-8 leading-relaxed">
            Automatically round up your everyday purchases and donate the spare change to your parish.
          </p>
          <Button
            onClick={() => setWizardStep(1)}
            className="bg-gold hover:bg-gold text-black font-semibold text-base px-8 h-12"
          >
            <Zap className="w-4 h-4 mr-2" />
            Set up roundup donations
          </Button>
        </div>
      </div>
    )
  }

  // ─── CONFIGURED VIEW ──────────────────────────────────────────────────────

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <h1 className="text-3xl font-bold font-display text-charcoal">Round-Ups</h1>
        {roundupSettings?.roundupsEnabled
          ? <span className="flex items-center gap-1.5 text-emerald-400 text-sm font-medium"><CheckCircle className="w-4 h-4" />Active</span>
          : <span className="flex items-center gap-1.5 text-red-600 text-sm font-medium"><XCircle className="w-4 h-4" />Disabled</span>
        }
      </div>

      {statusMessages}

      {/* ── Stat Cards ────────────────────────────────────────────────── */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        {/* Pending */}
        <div className="bg-white border border-surface-border rounded p-5">
          <div className="flex items-center gap-2 mb-2">
            <ArrowUpCircle className="w-4 h-4 text-gold" />
            <span className="text-charcoal-muted text-xs font-medium uppercase tracking-wide">Pending</span>
            {effectiveBoost > 1 && (
              <span className="text-[10px] font-bold bg-gold/20 text-gold px-1.5 py-0.5 rounded-full flex items-center gap-0.5">
                <Zap className="w-2.5 h-2.5" />{effectiveBoost}×
              </span>
            )}
          </div>
          <p className="text-2xl font-bold text-charcoal">
            {loadingTransactions ? '—' : formatCurrency(boostedUndonatedAmount)}
          </p>
          <p className="text-charcoal-faint text-xs mt-1">
            {undonatedCount} un-donated transaction{undonatedCount !== 1 ? 's' : ''}
          </p>
        </div>

        <div className="bg-white border border-surface-border rounded p-5">
          <div className="flex items-center gap-2 mb-2">
            <TrendingUp className="w-4 h-4 text-gold" />
            <span className="text-charcoal-muted text-xs font-medium uppercase tracking-wide">Transactions</span>
          </div>
          <p className="text-2xl font-bold text-charcoal">{loadingTransactions ? '—' : filteredTxns.length}</p>
          <p className="text-charcoal-faint text-xs mt-1">This week</p>
        </div>

        <div className="bg-white border border-surface-border rounded p-5">
          <div className="flex items-center gap-2 mb-2">
            <DollarSign className="w-4 h-4 text-gold" />
            <span className="text-charcoal-muted text-xs font-medium uppercase tracking-wide">Avg per Week</span>
          </div>
          <p className="text-2xl font-bold text-charcoal">
            {loadingRoundupTotal || allTimeRoundupCount === 0
              ? '—'
              : formatCurrency(allTimeRoundupTotal / allTimeRoundupCount)}
          </p>
          <p className="text-charcoal-faint text-xs mt-1">All-time average</p>
        </div>

        <div className="bg-white border border-surface-border rounded p-5">
          <div className="flex items-center gap-2 mb-2">
            <Wallet className="w-4 h-4 text-gold" />
            <span className="text-charcoal-muted text-xs font-medium uppercase tracking-wide">All-Time Total</span>
          </div>
          <p className="text-2xl font-bold text-charcoal">
            {loadingRoundupTotal ? '—' : formatCurrency(allTimeRoundupTotal)}
          </p>
          <p className="text-charcoal-faint text-xs mt-1">Total donated</p>
        </div>
      </div>

      {/* ── Next Donation ─────────────────────────────────────────────── */}
      {hasUpcomingDonation && (
        <div className="bg-white border border-surface-border rounded p-5 flex items-center justify-between gap-4">
          <div>
            <p className="text-charcoal-muted text-xs font-medium uppercase tracking-wide">Next donation</p>
            <p className="text-charcoal font-semibold text-lg mt-1">
              {nextRoundupSunday.toLocaleDateString(undefined, { weekday: 'long', month: 'short', day: 'numeric' })}
            </p>
            <p className="text-charcoal-muted text-sm">
              Estimated {formatCurrency(boostedUndonatedAmount)}
              {effectiveBoost > 1 && (
                <span className="text-gold ml-1.5 inline-flex items-center gap-0.5">
                  <Zap className="w-3 h-3" />{effectiveBoost}× boost applied
                </span>
              )}
            </p>
            <p className="text-charcoal-faint text-xs mt-2">
              Transactions from {formatWindowRange(nextRoundupWindow.start, nextRoundupWindow.end)}
            </p>
          </div>
        </div>
      )}

      {/* ── Edit Settings (collapsible) ───────────────────────────────── */}
      <div className="bg-white border border-surface-border rounded overflow-hidden">
        <button
          onClick={() => setShowEditSettings(!showEditSettings)}
          className="w-full flex items-center justify-between p-5 hover:bg-surface-linen transition-colors text-left"
        >
          <div className="flex items-center gap-2">
            <Settings className="w-5 h-5 text-gold" />
            <span className="font-semibold text-charcoal">Edit Settings</span>
            {roundupSettings?.roundupParish && !showEditSettings && (
              <span className="text-charcoal-faint text-sm">— {roundupSettings.roundupParish.name}</span>
            )}
          </div>
          {showEditSettings
            ? <ChevronUp className="w-4 h-4 text-charcoal-subtle" />
            : <ChevronDown className="w-4 h-4 text-charcoal-subtle" />
          }
        </button>

        {showEditSettings && (
          <div className="border-t border-surface-border p-5 space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* Parish */}
              <div>
                <label className="block text-sm text-charcoal-muted mb-2">
                  <Church className="w-4 h-4 inline mr-1 -mt-0.5" />
                  Donate roundups to
                </label>
                <ParishSelector
                  parishes={userParishes}
                  selectedId={selectedParishId}
                  onSelect={setSelectedParishId}
                />
              </div>

              {/* Payment + Boost */}
              <div className="space-y-5">
                {/* Payment Method */}
                <div>
                  <label className="block text-sm text-charcoal-muted mb-2">
                    <Wallet className="w-4 h-4 inline mr-1 -mt-0.5" />
                    Payment Method
                  </label>
                  {roundupSettings?.hasPaymentMethod && !showPaymentForm ? (
                    <div className="flex items-center gap-3">
                      <span className="text-emerald-400 text-sm flex items-center gap-1.5">
                        <CheckCircle className="w-4 h-4" /> Card saved
                      </span>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={handleSetupPaymentMethod}
                        disabled={loadingSetupIntent}
                        className="text-charcoal-muted hover:text-gold text-xs"
                      >
                        {loadingSetupIntent && <Loader2 className="w-3 h-3 animate-spin mr-1" />}
                        Change
                      </Button>
                    </div>
                  ) : (
                    <Button
                      onClick={handleSetupPaymentMethod}
                      disabled={loadingSetupIntent}
                      variant="outline"
                      size="sm"
                      className="border-gold/30 text-gold hover:bg-gold/10"
                    >
                      {loadingSetupIntent
                        ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Loading...</>
                        : <><CreditCard className="w-4 h-4 mr-2" />Add Payment Method</>
                      }
                    </Button>
                  )}
                  {showPaymentForm && setupIntentSecret && stripePromise && (
                    <div className="mt-3 bg-surface-linen border border-surface-border rounded p-4">
                      <Elements stripe={stripePromise} options={{ clientSecret: setupIntentSecret, appearance: STRIPE_APPEARANCE }}>
                        <PaymentMethodForm
                          onSuccess={handlePaymentMethodSaved}
                          onCancel={() => { setShowPaymentForm(false); setSetupIntentSecret(null) }}
                        />
                      </Elements>
                    </div>
                  )}
                </div>

                {/* Boost */}
                <div>
                  <label className="block text-sm text-charcoal-muted mb-2">
                    <Zap className="w-4 h-4 inline mr-1 -mt-0.5" />
                    Roundup Boost
                  </label>
                  <div className="flex items-center gap-2 flex-wrap">
                    {[1, 2, 3, 5, 10].map((factor) => (
                      <button
                        key={factor}
                        onClick={() => setBoostFactor(factor)}
                        className={`px-3 py-1.5 rounded text-sm font-semibold transition-all ${
                          boostFactor === factor
                            ? 'bg-gold text-black shadow-gold'
                            : 'bg-surface-linen text-charcoal-muted hover:bg-surface-border hover:text-charcoal border border-surface-border'
                        }`}
                      >
                        {factor === 1 ? '1×' : `${factor}×`}
                      </button>
                    ))}
                  </div>
                  {boostFactor > 1 && (
                    <p className="text-gold text-xs mt-1.5 flex items-center gap-1">
                      <Zap className="w-3 h-3" />{boostFactor}× — roundups multiplied by {boostFactor}
                    </p>
                  )}
                </div>
              </div>
            </div>

            {/* Linked Banks */}
            <div>
              <div className="flex items-center justify-between mb-3">
                <label className="text-sm text-charcoal-muted flex items-center gap-1.5">
                  <Building2 className="w-4 h-4 text-gold" />
                  Linked Banks
                </label>
                <FinancialConnectionsButton
                  onSuccess={handleLinkSuccess}
                  label="Link a Bank"
                  className="h-8 text-xs px-3 bg-gold hover:bg-gold text-black font-semibold rounded"
                />
              </div>
              {loadingBanks ? (
                <div className="flex items-center gap-2 py-3">
                  <Loader2 className="w-4 h-4 text-gold animate-spin" />
                  <span className="text-charcoal-muted text-sm">Loading banks...</span>
                </div>
              ) : banks.length === 0 ? (
                <p className="text-charcoal-faint text-sm">No banks linked.</p>
              ) : (
                <BankList banks={banks} unlinking={unlinking} onUnlink={handleUnlink} />
              )}
            </div>

            {/* Save / Enable / Disable */}
            <div className="flex items-center gap-3 pt-4 border-t border-surface-border flex-wrap">
              {roundupSettings?.roundupsEnabled ? (
                <>
                  {(boostFactor !== (roundupSettings?.roundupBoostFactor || 1) ||
                    (selectedParishId && selectedParishId !== roundupSettings?.roundupParishId)) && (
                    <Button
                      onClick={() => handleSaveSettings(true)}
                      disabled={
                        savingSettings ||
                        (selectedParishId !== roundupSettings?.roundupParishId && !selectedParishSelectable)
                      }
                      className="bg-gold hover:bg-gold text-black font-semibold"
                    >
                      {savingSettings && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                      Save Changes
                    </Button>
                  )}
                  <Button
                    onClick={() => handleSaveSettings(false)}
                    disabled={savingSettings}
                    variant="outline"
                    className="border-red-200 text-red-600 hover:bg-red-50"
                  >
                    {savingSettings
                      ? <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      : <XCircle className="w-4 h-4 mr-2" />
                    }
                    Disable Roundups
                  </Button>
                </>
              ) : (
                <Button
                  onClick={() => handleSaveSettings(true)}
                  disabled={!selectedParishSelectable || savingSettings}
                  className="bg-emerald-600 hover:bg-emerald-500 text-white font-semibold"
                >
                  {savingSettings
                    ? <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    : <CheckCircle className="w-4 h-4 mr-2" />
                  }
                  Re-enable Roundups
                </Button>
              )}
            </div>
          </div>
        )}
      </div>

      {/* ── Transaction History ───────────────────────────────────────── */}
      <div className="bg-white border border-surface-border rounded p-6">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2">
            <DollarSign className="w-5 h-5 text-gold" />
            <h2 className="text-xl font-semibold text-charcoal">Transaction History</h2>
            <span className="text-charcoal-faint text-sm ml-1">
              ({formatWindowRange(nextRoundupWindow.start, nextRoundupWindow.end)})
            </span>
          </div>
          <Button
            onClick={handleSync}
            disabled={syncing || banks.length === 0}
            variant="outline"
            size="sm"
            className="border-gold/30 text-gold hover:bg-gold/10"
          >
            {syncing
              ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Syncing...</>
              : <><RefreshCw className="w-4 h-4 mr-2" />Refresh</>
            }
          </Button>
        </div>

        {loadingTransactions ? (
          <div className="flex items-center justify-center py-8">
            <Loader2 className="w-6 h-6 text-gold animate-spin" />
            <span className="text-charcoal-muted ml-2">Loading transactions...</span>
          </div>
        ) : filteredTxns.length === 0 ? (
          <div className="text-center py-8">
            <DollarSign className="w-12 h-12 text-charcoal-faint mx-auto mb-3" />
            <p className="text-charcoal-subtle text-sm">No transactions yet.</p>
            <p className="text-charcoal-faint text-xs mt-1">
              {banks.length === 0
                ? 'Link a bank account to see your transactions.'
                : 'Transactions will appear after your first sync.'
              }
            </p>
          </div>
        ) : (
          <>
            {/* Table + fade overlay wrapper */}
            <div className="relative">
              <div className="overflow-x-auto">
                <table className="w-full text-left">
                  <thead>
                    <tr className="border-b border-surface-border">
                      <th className="text-charcoal-subtle text-xs font-medium uppercase tracking-wider pb-3 pr-4">Date</th>
                      <th className="text-charcoal-subtle text-xs font-medium uppercase tracking-wider pb-3 pr-4">Account</th>
                      <th className="text-charcoal-subtle text-xs font-medium uppercase tracking-wider pb-3 pr-4 text-right">Amount</th>
                      <th className="text-charcoal-subtle text-xs font-medium uppercase tracking-wider pb-3 pr-4 text-right">Round-Up</th>
                      <th className="text-charcoal-subtle text-xs font-medium uppercase tracking-wider pb-3 text-center">Status</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-surface-border">
                    {visibleTxns.map((txn) => (
                      <tr key={txn.id} className="hover:bg-surface-linen transition-colors">
                        <td className="py-3 pr-4 text-sm text-charcoal-muted whitespace-nowrap">
                          {formatDate(txn.date)}
                        </td>
                        <td className="py-3 pr-4 text-xs text-charcoal-faint">
                          {txn.accountName}
                          {txn.accountMask && <span className="ml-1">••{txn.accountMask}</span>}
                        </td>
                        <td className="py-3 pr-4 text-sm text-right font-mono text-charcoal">
                          {formatCurrency(txn.amount)}
                        </td>
                        <td className="py-3 pr-4 text-sm text-right font-mono">
                          {txn.roundupAmount > 0 ? (
                            <span className={`inline-flex items-center gap-1 ${txn.isDonated ? 'text-charcoal-faint' : 'text-emerald-400'}`}>
                              {formatCurrency(txn.roundupAmount)}
                              {effectiveBoost > 1 && (
                                <span className="text-[10px] font-bold bg-gold/20 text-gold px-1.5 py-0.5 rounded-full flex items-center gap-0.5">
                                  <Zap className="w-2.5 h-2.5" />{effectiveBoost}×
                                </span>
                              )}
                            </span>
                          ) : (
                            <span className="text-charcoal-faint">—</span>
                          )}
                        </td>
                        <td className="py-3 text-center">
                          {txn.isDonated ? (
                            <span className="text-[10px] text-emerald-400/80 bg-emerald-400/10 px-2 py-0.5 rounded-full">Donated</span>
                          ) : txn.roundupAmount > 0 ? (
                            <span className="text-[10px] text-amber-400/80 bg-amber-400/10 px-2 py-0.5 rounded-full">Upcoming</span>
                          ) : (
                            <span className="text-charcoal-faint text-[10px]">—</span>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Fade overlay when truncated */}
              {!showAllTransactions && filteredTxns.length > VISIBLE_TX_COUNT && (
                <div className="absolute bottom-0 left-0 right-0 h-16 bg-gradient-to-t from-white to-transparent pointer-events-none" />
              )}
            </div>

            {/* Show more / Show less */}
            {filteredTxns.length > VISIBLE_TX_COUNT && (
              <button
                onClick={() => setShowAllTransactions(!showAllTransactions)}
                className="w-full mt-3 py-2 text-sm font-semibold text-gold hover:text-gold/80 transition-colors flex items-center justify-center gap-1.5"
              >
                {showAllTransactions ? (
                  <><ChevronUp className="w-4 h-4" />Show less</>
                ) : (
                  <><ChevronDown className="w-4 h-4" />Show all {filteredTxns.length} transactions</>
                )}
              </button>
            )}
          </>
        )}
      </div>
    </div>
  )
}
